import { useState, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'
import ReactMarkdown from 'react-markdown'
import {
  CheckCircle2,
  Circle,
  Eye,
  Film,
  Loader2,
  Play,
  Square,
  Stamp,
  MessageSquareDiff,
  Paperclip,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import type { ProjectTask } from '@/types/conversations'

interface CodingWorker {
  id: number
  name: string
  status: string
}

interface BoardViewProps {
  tasks: ProjectTask[]
  codingWorkers?: CodingWorker[]
  provisioningWorkers?: string[]
  onStart: (taskId: number) => Promise<any>
  onMarkDone: (taskId: number) => void
  onStop: (taskId: number) => void
  onAccept: (taskId: number) => Promise<any>
  onDiscard: (taskId: number) => Promise<any>
  onRequestChanges?: (task: { id: number; title: string }) => void
  onDelete: (taskId: number) => void
  autoRun?: boolean
  onToggleAutoRun?: () => Promise<any>
  parallelAgentsEnabled?: boolean
  parallelWorkerCapacity?: number
  onToggleParallelAgents?: (value: boolean) => void
  verificationEnabled?: boolean
  onToggleVerification?: (value: boolean) => void
  managedAgentProject?: boolean
}

const COLUMNS = [
  { key: 'backlog' as const, i18nKey: 'taskBoard.columns.backlog', statuses: ['backlog', 'queued'] },
  { key: 'building' as const, i18nKey: 'taskBoard.columns.building', statuses: ['in_progress'] },
  { key: 'review' as const, i18nKey: 'taskBoard.columns.review', statuses: ['review'] },
]

export const BoardView = memo(function BoardView({ tasks, codingWorkers = [], provisioningWorkers = [], onStart, onMarkDone, onStop, onAccept, onDiscard, onRequestChanges, onDelete, autoRun = false, onToggleAutoRun, parallelAgentsEnabled = true, parallelWorkerCapacity = 0, onToggleParallelAgents, verificationEnabled = true, onToggleVerification, managedAgentProject = false }: BoardViewProps) {
  const { t } = useTranslation('conversations')
  const parallelToggleDisabled = !parallelAgentsEnabled && parallelWorkerCapacity <= 0

  // Derive real-time worker status from tasks (server prop is stale after page load)
  const workersWithStatus = codingWorkers.map(w => {
    const buildingTask = tasks.find(t => t.status === 'in_progress' && t.coding_worker_name === w.name)
    return { ...w, status: buildingTask ? 'working' : 'idle' }
  })
  const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null)
  const [taskToDelete, setTaskToDelete] = useState<ProjectTask | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)

  const handleDelete = (task: ProjectTask, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setTaskToDelete(task)
  }

  const confirmDelete = () => {
    if (taskToDelete) {
      onDelete(taskToDelete.id)
      setTaskToDelete(null)
      if (selectedTask?.id === taskToDelete.id) setSelectedTask(null)
    }
  }

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Board header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold">{t('taskBoard.title')}</h2>
              {tasks.length > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                  {tasks.length}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {/* Main agent badge — hidden when building */}
              {!tasks.some(t => t.target === 'main' && t.status === 'in_progress') && (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 gap-1">
                  <span className="h-1.5 w-1.5 rounded-sm bg-amber-400" />
                  Main
                </Badge>
              )}
              {/* Worker badges — hidden when building */}
              {workersWithStatus.filter(w => w.status !== 'working').map(w => (
                <Badge
                  key={w.id}
                  variant="outline"
                  className={`text-[9px] px-1.5 py-0 h-4 gap-1 capitalize ${
                    parallelAgentsEnabled ? '' : 'border-dashed text-muted-foreground opacity-60'
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-sm ${parallelAgentsEnabled ? 'bg-blue-400' : 'bg-muted-foreground/50'}`} />
                  {w.name}
                </Badge>
              ))}
              {/* Provisioning badges — show while workers are being set up */}
              {provisioningWorkers
                .filter(name => !codingWorkers.some(w => w.name === name))
                .map(name => (
                <Badge
                  key={`prov-${name}`}
                  variant="outline"
                  className="text-[9px] px-1.5 py-0 h-4 gap-1 capitalize text-muted-foreground"
                >
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                  {name}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {onToggleParallelAgents && !managedAgentProject && (
              <div className="flex items-center gap-2">
                <Label htmlFor="parallel-agents" className="text-[11px] text-muted-foreground cursor-pointer">
                  {t('taskBoard.parallelAgentsLabel')}
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Switch
                        id="parallel-agents"
                        checked={parallelAgentsEnabled}
                        disabled={parallelToggleDisabled}
                        onCheckedChange={onToggleParallelAgents}
                        className="scale-75"
                      />
                    </span>
                  </TooltipTrigger>
                  {parallelToggleDisabled && (
                    <TooltipContent>{t('taskBoard.parallelAgentsDisabled')}</TooltipContent>
                  )}
                </Tooltip>
              </div>
            )}
            {onToggleVerification && !managedAgentProject && (
              <div className="flex items-center gap-2">
                <Label htmlFor="verification" className="text-[11px] text-muted-foreground cursor-pointer">
                  {t('taskBoard.verificationLabel')}
                </Label>
                <Switch
                  id="verification"
                  checked={verificationEnabled}
                  onCheckedChange={onToggleVerification}
                  className="scale-75"
                />
              </div>
            )}
            {onToggleAutoRun && (
              <div className="flex items-center gap-2">
                <Label htmlFor="auto-run" className="text-[11px] text-muted-foreground cursor-pointer">
                  {t('taskBoard.autoRunLabel')}
                </Label>
                <Switch
                  id="auto-run"
                  checked={autoRun}
                  onCheckedChange={() => onToggleAutoRun()}
                  className="scale-75"
                />
              </div>
            )}
          </div>
        </div>

      <div className="flex flex-1 gap-3 p-4 overflow-auto min-h-0">
        {COLUMNS.map(column => {
          const columnTasks = tasks.filter(t => column.statuses.includes(t.status))

          return (
            <div key={column.key} className="flex flex-col min-w-[220px] flex-1 min-h-0">
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t(column.i18nKey)}
                </span>
                {columnTasks.length > 0 && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                    {columnTasks.length}
                  </Badge>
                )}
              </div>

              <div className="flex-1 space-y-2 overflow-y-auto min-h-0">
                {columnTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    taskTodos={task.todos}
                    onClick={() => setSelectedTask(task)}
                    onStart={onStart}
                    onStop={onStop}
                    onMarkDone={onMarkDone}
                    onAccept={onAccept}
                    onDiscard={onDiscard}
                    onRequestChanges={onRequestChanges ? () => onRequestChanges({ id: task.id, title: task.title }) : undefined}
                    onDelete={(e) => handleDelete(task, e)}
                    onVideoClick={(url) => setVideoUrl(url)}
                  />
                ))}

                {columnTasks.length === 0 && (
                  <div className="rounded-lg border border-dashed border-border/60 flex items-center justify-center py-7">
                    <span className="text-[11px] text-muted-foreground/50">{t('taskBoard.noTasks')}</span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      </div>

      {/* Task detail sheet */}
      <Sheet open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
        <SheetContent className="sm:max-w-lg overflow-y-auto pb-8">
          {selectedTask && (
            <>
              <SheetHeader className="pr-10 pt-6">
                <div className="flex gap-2 items-start">
                  <div className="h-[22px] flex items-center flex-shrink-0">
                    <StatusIcon status={selectedTask.status} />
                  </div>
                  <SheetTitle className="text-left flex-1 leading-snug">{selectedTask.title}</SheetTitle>
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <StatusBadge status={selectedTask.status} />
                  <span className="text-[10px] text-muted-foreground">
                    {t('taskBoard.sheet.created', { date: new Date(selectedTask.created_at).toLocaleDateString() })}
                  </span>
                </div>
              </SheetHeader>

              {/* Todo list from task — shown first for visibility */}
              {selectedTask.todos.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="px-6">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      {t('taskBoard.sheet.progress')} ({selectedTask.todos.filter(t => t.status === 'completed').length}/{selectedTask.todos.length})
                    </h4>
                    <div className="space-y-1.5">
                      {selectedTask.todos.filter(t => t.status === 'in_progress').map((todo, i) => (
                        <div key={`ip-${i}`} className="flex items-center gap-2 text-sm">
                          <Loader2 className="h-3.5 w-3.5 text-primary animate-spin flex-shrink-0" />
                          <span className="font-medium">{todo.content}</span>
                        </div>
                      ))}
                      {selectedTask.todos.filter(t => t.status === 'pending').map((todo, i) => (
                        <div key={`p-${i}`} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Circle className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>{todo.content}</span>
                        </div>
                      ))}
                      {selectedTask.todos.filter(t => t.status === 'completed').map((todo, i) => (
                        <div key={`c-${i}`} className="flex items-center gap-2 text-sm text-muted-foreground line-through">
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                          <span>{todo.content}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Attached files */}
              {selectedTask.files?.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="px-6">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      Attachments ({selectedTask.files.length})
                    </h4>
                    <div className="space-y-2">
                      {selectedTask.files.map((file, i) => (
                        <div key={i} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                          {file.thumbnail_url ? (
                            <img
                              src={file.thumbnail_url}
                              alt={file.filename}
                              className="h-10 w-10 rounded object-cover flex-shrink-0 border border-border/50"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0 border border-border/50">
                              <Paperclip className="h-4 w-4" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm truncate">{file.filename}</p>
                            <p className="text-[10px]">
                              {file.byte_size < 1024 ? `${file.byte_size} B` : file.byte_size < 1048576 ? `${Math.round(file.byte_size / 1024)} KB` : `${(file.byte_size / 1048576).toFixed(1)} MB`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 mt-6 px-6">
                {(selectedTask.status === 'backlog' || selectedTask.status === 'queued') && (
                  <Button
                    size="sm"
                    onClick={async () => {
                      const result = await onStart(selectedTask.id)
                      if (result?.error) {
                        toast.error(result.error)
                      } else {
                        setSelectedTask(null)
                      }
                    }}
                  >
                    <Play className="h-3.5 w-3.5" />
                    {t('taskBoard.actions.startBuilding')}
                  </Button>
                )}
                {selectedTask.status === 'in_progress' && (
                  <Button size="sm" variant="destructive" onClick={() => { onStop(selectedTask.id); setSelectedTask(null) }}>
                    <Square className="h-3 w-3 fill-current" />
                    {t('taskBoard.actions.stop')}
                  </Button>
                )}
                {selectedTask.status === 'review' && selectedTask.coding_worker_id && (
                  <>
                    <Button
                      size="sm"
                      onClick={async () => {
                        const result = await onAccept(selectedTask.id)
                        if (result?.error) {
                          toast.error(result.error)
                        } else {
                          setSelectedTask(null)
                        }
                      }}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {t('taskBoard.actions.accept')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        const result = await onDiscard(selectedTask.id)
                        if (result?.error) {
                          toast.error(result.error)
                        } else {
                          setSelectedTask(null)
                        }
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                      {t('taskBoard.actions.discard')}
                    </Button>
                  </>
                )}
                {selectedTask.status === 'review' && !selectedTask.coding_worker_id && (
                  <Button size="sm" className="gap-1.5" onClick={() => { onMarkDone(selectedTask.id); setSelectedTask(null) }}>
                    <Stamp className="h-3.5 w-3.5" />
                    {t('taskBoard.actions.markDoneSheet')}
                  </Button>
                )}
                {selectedTask.status === 'review' && onRequestChanges && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-200 text-amber-600/80 hover:bg-amber-50 hover:text-amber-700 dark:border-amber-800 dark:text-amber-500/70 dark:hover:bg-amber-950/50"
                    onClick={() => {
                      onRequestChanges({ id: selectedTask.id, title: selectedTask.title })
                      setSelectedTask(null)
                    }}
                  >
                    <MessageSquareDiff className="h-3.5 w-3.5" />
                    {t('taskBoard.actions.requestChanges')}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500 hover:text-red-600 gap-1.5"
                  onClick={() => handleDelete(selectedTask)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t('taskBoard.actions.delete')}
                </Button>
              </div>

              {/* Description */}
              {selectedTask.description && (
                <>
                  <Separator className="my-4" />
                  <div className="px-6">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('taskBoard.sheet.description')}</h4>
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown>{selectedTask.description}</ReactMarkdown>
                    </div>
                  </div>
                </>
              )}

              {/* Change requests */}
              {selectedTask.change_requests.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="px-6">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      {t('taskBoard.sheet.changeRequests')} ({selectedTask.change_requests.length})
                    </h4>
                    <div className="space-y-3">
                      {selectedTask.change_requests.map((cr, i) => (
                        <div key={i} className="pl-3 border-l-2 border-amber-300 dark:border-amber-600">
                          <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
                            <ReactMarkdown>{cr.content}</ReactMarkdown>
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(cr.created_at).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Browser test recording */}
              {selectedTask.recording_url ? (
                <>
                  <Separator className="my-4" />
                  <div className="px-6">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      <Film className="h-3 w-3 inline mr-1" />
                      {t('taskBoard.sheet.testRecording')}
                    </h4>
                    <div
                      className="relative cursor-pointer group/sheetvideo"
                      onClick={() => setVideoUrl(selectedTask.recording_url)}
                    >
                      <video
                        src={selectedTask.recording_url}
                        controls
                        className="w-full rounded-lg border bg-black"
                        preload="metadata"
                        onLoadedMetadata={(e) => { (e.target as HTMLVideoElement).playbackRate = 1.5 }}
                      />
                      <div className="absolute top-2 right-2 opacity-0 group-hover/sheetvideo:opacity-100 transition-opacity">
                        <Badge className="text-[9px] px-1.5 py-0.5 bg-black/60 text-white border-0 cursor-pointer">
                          {t('taskBoard.sheet.fullscreen')}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </>
              ) : selectedTask.recording_pending ? (
                <>
                  <Separator className="my-4" />
                  <div className="px-6">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      <Film className="h-3 w-3 inline mr-1" />
                      {t('taskBoard.sheet.testRecording')}
                    </h4>
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-dashed bg-muted/30">
                      <Loader2 className="h-4 w-4 text-muted-foreground animate-spin flex-shrink-0" />
                      <span className="text-xs text-muted-foreground">{t('taskBoard.card.preparingRecording')}</span>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!taskToDelete} onOpenChange={(open) => !open && setTaskToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('taskBoard.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('taskBoard.deleteDialog.description', { title: taskToDelete?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('taskBoard.deleteDialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('taskBoard.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fullscreen video modal */}
      <Dialog open={!!videoUrl} onOpenChange={(open) => !open && setVideoUrl(null)}>
        <DialogContent className="max-w-[80vw] max-h-[80vh] p-0 overflow-hidden bg-black border-0">
          {videoUrl && (
            <video
              src={videoUrl}
              controls
              autoPlay
              className="w-full h-full max-h-[80vh] object-contain"
              onLoadedMetadata={(e) => { (e.target as HTMLVideoElement).playbackRate = 1.5 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}, (prev, next) => {
  // Only re-render when task data or autoRun changes.
  // Callback identity changes (from parent re-renders) are ignored.
  return prev.tasks === next.tasks
    && prev.autoRun === next.autoRun
    && prev.parallelAgentsEnabled === next.parallelAgentsEnabled
    && prev.parallelWorkerCapacity === next.parallelWorkerCapacity
    && prev.verificationEnabled === next.verificationEnabled
    && prev.managedAgentProject === next.managedAgentProject
    && prev.codingWorkers === next.codingWorkers
    && prev.provisioningWorkers === next.provisioningWorkers
})

const TaskCard = memo(function TaskCard({
  task,
  taskTodos,
  onClick,
  onStart,
  onStop,
  onMarkDone,
  onAccept,
  onDiscard,
  onRequestChanges,
  onDelete,
  onVideoClick,
}: {
  task: ProjectTask
  taskTodos?: Array<{ content: string; status: string }>
  onClick: () => void
  onStart: (taskId: number) => Promise<any>
  onStop: (taskId: number) => void
  onMarkDone: (taskId: number) => void
  onAccept: (taskId: number) => Promise<any>
  onDiscard: (taskId: number) => Promise<any>
  onRequestChanges?: () => void
  onDelete: (e: React.MouseEvent) => void
  onVideoClick?: (url: string) => void
}) {
  const { t } = useTranslation('conversations')
  const [isLoading, setIsLoading] = useState(false)
  const todos = taskTodos || []
  const currentTodo = todos.find(t => t.status === 'in_progress') || todos.find(t => t.status === 'pending')
  const totalTodos = todos.length
  const completedTodos = todos.filter(t => t.status === 'completed').length

  const handleStart = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsLoading(true)
    const result = await onStart(task.id)
    if (result?.error) {
      toast.error(result.error)
    }
    setIsLoading(false)
  }

  const handleAction = async (e: React.MouseEvent, action: (id: number) => void | Promise<any>) => {
    e.stopPropagation()
    setIsLoading(true)
    await action(task.id)
    setIsLoading(false)
  }

  return (
    <div
      className="relative rounded-lg border bg-card shadow-sm cursor-pointer hover:border-foreground/20 transition-colors group"
      onClick={onClick}
    >
      {/* Delete — absolute top-right, hover reveal */}
      <button
        className="absolute top-2 right-2 h-5 w-5 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 text-muted-foreground/60 hover:text-red-500 hover:bg-red-500/10 transition-all z-10"
        onClick={(e) => onDelete(e)}
        title={t('taskBoard.actions.deleteTask')}
      >
        <Trash2 className="h-3 w-3" />
      </button>

      {/* Content — grid layout: icon column + content column */}
      <div className="px-3 pt-2.5 pb-2 grid grid-cols-[18px_1fr] gap-x-2 gap-y-1">
        {/* Row 1: status icon + title */}
        <div className="h-[18px] flex items-center">
          <StatusIcon status={task.status} />
        </div>
        <div className="flex items-start gap-1 pr-5">
          <p className="text-[13px] font-medium leading-snug line-clamp-2">{task.title}</p>
          {task.files?.length > 0 && (
            <Paperclip className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5" />
          )}
        </div>

        {/* Row 2: agent badge + recording/todo (all in content column) */}
        {(task.status === 'in_progress' || task.recording_url || task.recording_pending || currentTodo) && (
          <>
            {/* Empty icon column */}
            <div />
            <div className="flex flex-col gap-1">
              {/* Agent badge */}
              {task.status === 'in_progress' && (
                <div>
                  {task.target === 'main' ? (
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-3.5 gap-1">
                      <span className="h-1.5 w-1.5 rounded-sm bg-amber-500 animate-pulse" />
                      Main
                    </Badge>
                  ) : task.coding_worker_name ? (
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-3.5 gap-1 capitalize">
                      <span className="h-1.5 w-1.5 rounded-sm bg-green-500 animate-pulse" />
                      {task.coding_worker_name}
                    </Badge>
                  ) : null}
                </div>
              )}
              {/* Recording preview */}
              {task.recording_url ? (
                <div
                  className="relative rounded overflow-hidden cursor-pointer group/video"
                  onClick={(e) => {
                    e.stopPropagation()
                    onVideoClick?.(task.recording_url!)
                  }}
                >
                  <video
                    src={task.recording_url}
                    muted
                    preload="metadata"
                    className="w-full h-20 object-cover bg-black rounded"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <Play className="h-6 w-6 text-white fill-white" />
                  </div>
                  <div className="absolute bottom-1 left-1">
                    <Badge className="text-[8px] px-1 py-0 h-3 bg-black/60 text-white border-0">
                      <Film className="h-2 w-2 mr-0.5" />
                      Test
                    </Badge>
                  </div>
                </div>
              ) : task.recording_pending ? (
                <div className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-muted/50 border border-dashed">
                  <Loader2 className="h-3 w-3 text-muted-foreground animate-spin flex-shrink-0" />
                  <span className="text-[10px] text-muted-foreground">{t('taskBoard.card.preparingRecording')}</span>
                </div>
              ) : null}
              {/* Current todo */}
              {currentTodo && (
                <div className="flex items-start gap-1.5">
                  <Loader2 className="h-3 w-3 text-primary animate-spin flex-shrink-0 mt-0.5" />
                  <span className="text-[10px] text-muted-foreground break-words">{currentTodo.content}</span>
                  {totalTodos > 0 && (
                    <Badge variant="secondary" className="text-[9px] px-1 py-0 h-3.5 flex-shrink-0 ml-auto">
                      {completedTodos}/{totalTodos}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Actions — aligned with content column */}
      <div className="pl-[calc(18px+0.5rem+0.75rem)] pr-3 pb-2.5 flex flex-wrap gap-1.5">
        {(task.status === 'backlog' || task.status === 'queued') && (
          <Button
            size="sm"
            className="h-6 text-[11px] px-2.5"
            disabled={isLoading}
            onClick={handleStart}
          >
            {isLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}
            {t('taskBoard.actions.start')}
          </Button>
        )}
        {task.status === 'in_progress' && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 text-[11px] px-2.5 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600 dark:border-red-800 dark:hover:bg-red-950"
            disabled={isLoading}
            onClick={(e) => handleAction(e, onStop)}
          >
            {isLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Square className="h-2.5 w-2.5 mr-1 fill-current" />}
            {t('taskBoard.actions.stop')}
          </Button>
        )}
        {task.status === 'review' && task.coding_worker_id && (
          <div className="flex gap-1.5">
            <Button
              size="sm"
              className="h-6 text-[11px] px-2.5"
              disabled={isLoading}
              onClick={(e) => handleAction(e, onAccept)}
            >
              {isLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
              {t('taskBoard.actions.accept')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[11px] px-2.5"
              disabled={isLoading}
              onClick={(e) => handleAction(e, onDiscard)}
            >
              <X className="h-3 w-3 mr-1" />
            </Button>
          </div>
        )}
        {task.status === 'review' && !task.coding_worker_id && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 text-[11px] px-2.5 gap-1 border-dashed text-green-600 border-green-300 hover:bg-green-50 hover:text-green-700 dark:border-green-700 dark:hover:bg-green-950"
            disabled={isLoading}
            onClick={(e) => handleAction(e, onMarkDone)}
          >
            {isLoading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Stamp className="h-2.5 w-2.5" />}
            {t('taskBoard.actions.markDone')}
          </Button>
        )}
        {task.status === 'review' && onRequestChanges && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 text-[11px] px-2 gap-1 border-amber-200 text-amber-600/80 hover:bg-amber-50 hover:text-amber-700 dark:border-amber-800 dark:text-amber-500/70 dark:hover:bg-amber-950/50"
            onClick={(e) => { e.stopPropagation(); onRequestChanges() }}
          >
            <MessageSquareDiff className="h-2.5 w-2.5" />
            {t('taskBoard.actions.requestChanges')}
          </Button>
        )}
      </div>
    </div>
  )
}, (prev, next) => {
  // Only re-render when the task data actually changed
  return prev.task === next.task && prev.taskTodos === next.taskTodos
})

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'in_progress':
      return <Loader2 className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
    case 'review':
      return <Eye className="h-4 w-4 text-amber-500 flex-shrink-0" />
    case 'backlog':
    default:
      return <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
  }
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('conversations')
  const config = {
    backlog: { i18nKey: 'taskBoard.columns.backlog', className: 'bg-muted text-muted-foreground' },
    in_progress: { i18nKey: 'taskBoard.columns.building', className: 'bg-primary/10 text-primary' },
    review: { i18nKey: 'taskBoard.columns.review', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  }[status] || { i18nKey: '', className: '' }

  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${config.className}`}>
      {config.i18nKey ? t(config.i18nKey) : status}
    </Badge>
  )
}
